import { useState, useEffect } from 'react';
import { marketplaceService } from '../../_services/marketplace.service';
import { DESIGNS } from '../gift-cards-v2/data';
import { apiCatalogToVoucher, mergeDesigns } from '../gift-cards-v2/giftCardMapper';
import { getGiftCardChannels } from '../gift-cards-v2/giftCardChannels';

// useGiftCardShopData — loads everything the customer gift-card shop needs for
// a salon: its public identity (name, currency, logo) and the gift-card
// catalogue. Voucher PRODUCTS come from the public salon summary (real catalog
// docs, online + active only — so the voucherId used at purchase is a real
// catalog id). DESIGNS = the static editorial/abstract base library with the
// salon's persisted availability overrides + custom designs merged on top
// (also from the public summary), curated per voucher via voucher.designIds.
const BASE_DESIGNS = DESIGNS.filter(d => !d.isCustom);

const useGiftCardShopData = (salonIdOrSlug) => {
  const [state, setState] = useState({
    loading: true,
    salonName: 'Your Salon',
    currency: '£',
    salonLogo: null,
    vouchers: [],
    designs: BASE_DESIGNS,
  });

  useEffect(() => {
    let alive = true;
    const run = async () => {
      let salonName = 'Your Salon';
      let currency = '£';
      let salonLogo = null;
      let vouchers = [];
      let designs = BASE_DESIGNS;
      try {
        const resp = await marketplaceService.getSalonSummary({ salonId: salonIdOrSlug });
        const salon = resp?.salon || resp;
        if (salon) {
          salonName = salon.salonName || salon.name || salonName;
          currency = salon.currencySymbol || salon.currency || currency;
          salonLogo = salon.image || salon.logo || null;
        }
        // Public vouchers are catalog docs (online + active). Map to the FE
        // voucher shape; the BE already filters to online + active.
        const apiVouchers = resp?.vouchers || (salon && salon.vouchers) || [];
        vouchers = apiVouchers.map(v => apiCatalogToVoucher({ ...v, id: v._id || v.id }));
        // Merge the salon's persisted designs (availability overrides + custom
        // designs) onto the static base, then show only available ones.
        const persistedDesigns = resp?.voucherDesigns || (salon && salon.voucherDesigns) || [];
        designs = mergeDesigns(BASE_DESIGNS, persistedDesigns.map(d => ({ ...d, id: d.designId || d.id })))
          .filter(d => d.available);
      } catch (e) {
        // Non-fatal: the shop renders with sensible defaults if the summary
        // can't be fetched (e.g. dev/unknown slug). Vouchers stay empty.
        console.log('[GiftCardShop] salon summary unavailable, using defaults:', e?.message);
      }

      // Salon-wide master switch: if the salon has turned off online gift-card
      // selling, the shop offers nothing (regardless of per-voucher flags).
      // Empty vouchers reuses the modal's "not selling online right now" state.
      // TODO(backend): read this from the public salon summary, not the local
      // mock store (see giftCardChannels.js).
      if (!getGiftCardChannels().online) vouchers = [];

      if (alive) {
        setState({ loading: false, salonName, currency, salonLogo, vouchers, designs });
      }
    };
    run();
    return () => { alive = false; };
  }, [salonIdOrSlug]);

  return state;
};

export default useGiftCardShopData;
